import { cellToText } from './dbCellText';

/**
 * Find-in-results for the DB Manager grid.
 *
 * This searches the rows already loaded into the page, not the table — it is
 * the Ctrl+F you expect from a text editor, not a WHERE clause. Narrowing the
 * *result set* is what the filter bar and "Filter by this cell" are for; this
 * is for locating a value inside what you are already looking at.
 *
 * Pure functions only, so the behaviour is testable without a grid.
 */

// A search over 10k rows x 60 columns can match hundreds of thousands of
// cells, and every match costs a Set entry and a highlighted <td>. Past a few
// thousand the list has stopped being a navigation aid anyway, so collection
// stops and the caller says so.
export const FIND_MATCH_CAP = 2000;

/**
 * Cells whose text contains `query`, in row-major order (the order you'd read
 * them, so "next match" walks left-to-right then down).
 *
 * `rowLimit` exists because the grid only ever holds MAX_RENDER_ROWS — a match
 * past that point could be counted but never reached, which reads as a broken
 * counter. Searching the same rows the grid holds keeps the count and the
 * highlights describing the same thing.
 *
 * "Holds", not "renders": the grid virtualizes, so at any instant it has only a
 * window of rows in the DOM. That is not the boundary — a windowed row is one
 * `scrollToIndex` away and is perfectly reachable. MAX_RENDER_ROWS is the cap on
 * what the grid will address at all, and that is what makes a match beyond it
 * unreachable.
 *
 * Returns {matches, truncated, rowsSearched} rather than a bare array so the
 * bar can distinguish "17 matches" from "the first 2000 of them".
 */
export function findMatches(
  rows,
  query,
  { matchCase = false, rowLimit = Infinity, columns = null, rowIndexes = null } = {}
) {
  const empty = { matches: [], truncated: false, rowsSearched: 0 };
  if (!Array.isArray(rows) || typeof query !== 'string' || query === '') return empty;

  const needle = matchCase ? query : query.toLowerCase();
  /**
   * `rowIndexes` is the row-axis twin of `columns`: the exact rows the grid is
   * drawing, when that is not simply "the first N".
   *
   * The record view draws only the SELECTED rows — as columns — so a match in
   * an unselected row is unreachable there in exactly the way a match in a
   * hidden column is. Both would be counted by the "n of m" bar and neither
   * could be highlighted or stepped onto.
   *
   * Ascending, because match order is the order you read them in and "next
   * match" has to walk forwards.
   */
  const scan = rowIndexes
    ? Array.from(rowIndexes)
        .filter((r) => Number.isInteger(r) && r >= 0 && r < rows.length)
        .sort((a, b) => a - b)
    : null;
  const searchable = rowLimit === Infinity ? rows.length : Math.min(rows.length, rowLimit);
  const total = scan ? scan.length : searchable;
  const matches = [];
  let truncated = false;

  for (let n = 0; n < total; n += 1) {
    const row = scan ? scan[n] : n;
    const cells = rows[row];
    if (!Array.isArray(cells)) continue;
    // `columns`, when given, restricts the search to the columns the grid is
    // actually drawing. A match in a hidden column can neither be highlighted
    // nor scrolled to, so counting it makes the "n of m" counter describe
    // something the user cannot reach — exactly what rowLimit exists to
    // prevent on the other axis.
    const width = columns ? columns.length : cells.length;
    for (let i = 0; i < width; i += 1) {
      const col = columns ? columns[i] : i;
      if (col >= cells.length) continue;
      const text = cellToText(cells[col]);
      // Skip empties before lowercasing them. What actually keeps a search for
      // "null" from hitting every blank cell is cellToText mapping NULL to the
      // empty string rather than to the word the grid draws — this line only
      // saves the work.
      if (text === '') continue;
      const hay = matchCase ? text : text.toLowerCase();
      if (hay.includes(needle)) {
        if (matches.length >= FIND_MATCH_CAP) {
          truncated = true;
          // How many rows were SEARCHED, which is the loop counter — with
          // `rowIndexes` the row number is an address, not a count.
          return { matches, truncated, rowsSearched: n };
        }
        matches.push({ row, col });
      }
    }
  }

  return { matches, truncated, rowsSearched: total };
}

/**
 * Index of the next/previous match, wrapping at both ends.
 *
 * Wrapping is what makes Enter-Enter-Enter a usable way to walk a result set,
 * and returning -1 for an empty list keeps "no matches" out of the callers.
 */
export function stepMatch(total, current, delta) {
  if (!Number.isFinite(total) || total <= 0) return -1;
  if (!Number.isFinite(current) || current < 0) return delta < 0 ? total - 1 : 0;
  return ((current + delta) % total + total) % total;
}

/**
 * The match at or after `row`, so reopening the bar or re-running a search
 * lands near where you were rather than jumping back to row 0.
 */
export function firstMatchAtOrAfterCell(matches, pos) {
  if (!Array.isArray(matches) || matches.length === 0) return -1;
  if (!pos || !Number.isFinite(pos.row)) return 0;
  const col = Number.isFinite(pos.col) ? pos.col : -1;
  const found = matches.findIndex((m) => m.row > pos.row || (m.row === pos.row && m.col >= col));
  return found === -1 ? 0 : found; // past the last match: wrap to the first
}

/**
 * Rows as the grid SHOWS them: staged edits (`pendingEdits`, "row:col" → value)
 * over the fetched values — what a search must match, as in spreadsheets and
 * DataGrip (owner, 30.09.2026). Unchanged rows are shared, not copied.
 */
export function rowsAsShown(rows, pendingEdits) {
  const keys = pendingEdits ? Object.keys(pendingEdits) : [];
  if (!Array.isArray(rows) || keys.length === 0) return rows;
  const out = rows.slice();
  for (const key of keys) {
    const [r, c] = key.split(':').map(Number);
    if (!Array.isArray(rows[r])) continue;
    if (out[r] === rows[r]) out[r] = rows[r].slice();
    out[r][c] = pendingEdits[key];
  }
  return out;
}

export function firstMatchAtOrAfter(matches, row) {
  if (!Array.isArray(matches) || matches.length === 0) return -1;
  if (!Number.isFinite(row)) return 0;
  const found = matches.findIndex((m) => m.row >= row);
  return found === -1 ? 0 : found;
}
